import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  lazy,
  Suspense,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, LayoutList } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import ViewModeToggle from './ViewModeToggle';
import { PROVIDER_BRAND } from '@/data/provider-brand';
import type { ViewMode, BrandAssets } from '@/types/roadmap.types';

const SlideReorderPanel = lazy(() => import('./SlideReorderPanel'));

const IS_DEV = import.meta.env.DEV;

interface SlideDefinition {
  key: string;
  label: string;
  component: ReactElement;
}

interface DeckShellProps {
  slides: SlideDefinition[];
  title: string;
  subtitle?: string;
  altLink?: { to: string; label: string };
  mode: ViewMode;
  onModeChange: (mode: ViewMode) => void;
  clientBrand?: BrandAssets;
  whiteLabel?: boolean;
  /** All slide keys including disabled ones, for the slides panel */
  slideOrder?: string[];
  /** Full registry labels (key -> label) for the slides panel */
  slideLabels?: Record<string, string>;
  /** Keys of slides that are currently disabled/conditional-null */
  disabledSlides?: Set<string>;
  /** Keys of slides that the user has hidden from the deck + PDF */
  hiddenSlides?: Set<string>;
  onToggleHidden?: (key: string) => void;
  onSlideReorder?: (newOrder: string[]) => void;
  companyId?: string;
}

export default function DeckShell({
  slides,
  title,
  subtitle,
  altLink,
  mode,
  onModeChange,
  clientBrand,
  whiteLabel = false,
  slideOrder,
  slideLabels,
  disabledSlides,
  hiddenSlides,
  onToggleHidden,
  onSlideReorder,
  companyId,
}: DeckShellProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState<1 | -1>(1);
  const [reorderOpen, setReorderOpen] = useState(false);
  const totalSlides = slides.length;
  const progress = ((currentSlide + 1) / totalSlides) * 100;

  const goTo = useCallback(
    (idx: number) => {
      if (idx < 0 || idx >= totalSlides) return;
      setDirection(idx > currentSlide ? 1 : -1);
      setCurrentSlide(idx);
    },
    [currentSlide, totalSlides],
  );

  const next = useCallback(
    () => goTo(currentSlide + 1),
    [goTo, currentSlide],
  );
  const prev = useCallback(
    () => goTo(currentSlide - 1),
    [goTo, currentSlide],
  );

  useEffect(() => {
    if (mode !== 'presentation') return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        next();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [mode, next, prev]);

  const [isPrinting, setIsPrinting] = useState(false);
  const isPresentation = mode === 'presentation' && !isPrinting;

  useEffect(() => {
    if (totalSlides === 0) return;
    if (currentSlide >= totalSlides) {
      setCurrentSlide(Math.max(0, totalSlides - 1));
    }
  }, [totalSlides, currentSlide]);

  const brandStyles = useMemo<CSSProperties>(() => {
    const colors = clientBrand?.colors;
    if (!colors) return {};
    return {
      '--brand-primary': colors.primary,
      ...(colors.secondary ? { '--brand-secondary': colors.secondary } : {}),
      ...(colors.accent ? { '--brand-accent': colors.accent } : {}),
    } as CSSProperties;
  }, [clientBrand]);

  const navLogo = PROVIDER_BRAND.logo;

  const reorderItems = useMemo(() => {
    if (!slideOrder || !slideLabels) return [];
    return slideOrder.map((key) => ({
      key,
      label: slideLabels[key] ?? key,
      disabled: disabledSlides?.has(key) ?? false,
      hidden: hiddenSlides?.has(key) ?? false,
    }));
  }, [slideOrder, slideLabels, disabledSlides, hiddenSlides]);

  const currentSlideKey = slides[currentSlide]?.key;

  void direction;

  return (
    <div
      className={cn(
        'font-manrope',
        isPresentation
          ? 'relative h-screen w-screen overflow-hidden flex flex-col'
          : 'min-h-screen flex flex-col',
      )}
      style={brandStyles}
    >
      {/* Navigation bar */}
      <nav
        className={cn(
          'no-print z-50 border-b border-border/40',
          isPresentation
            ? 'sticky top-0 bg-white/80 backdrop-blur-md'
            : 'sticky top-0 bg-white/90 backdrop-blur-sm',
        )}
      >
        <div className="flex items-center justify-between h-14 px-4 sm:px-6">
          {/* Left: provider logo + title */}
          <div className="flex items-center gap-3 min-w-0">
            {!whiteLabel && (
              <>
                <img
                  src={navLogo}
                  alt={PROVIDER_BRAND.companyName}
                  className="h-7 shrink-0"
                />
                <div className="hidden sm:block h-5 w-px bg-border/60" />
              </>
            )}
            <div className="min-w-0 hidden sm:block">
              <p className="text-sm font-medium text-foreground truncate">
                {title}
              </p>
              {subtitle && (
                <p className="text-[11px] text-muted-foreground truncate leading-tight">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Center: slide counter (presentation only) */}
          {isPresentation && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={prev}
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-semibold tracking-wide text-foreground tabular-nums">
                {currentSlide + 1}{' '}
                <span className="text-muted-foreground font-normal">of</span>{' '}
                {totalSlides}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={next}
                disabled={currentSlide === totalSlides - 1}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          )}

          {/* Right: mode toggle + links + slides panel button */}
          <div className="flex items-center gap-2">
            {onToggleHidden && reorderItems.length > 0 && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setReorderOpen((v) => !v)}
                title="Show / hide slides"
                aria-label="Show / hide slides"
              >
                <LayoutList className="w-4 h-4" />
              </Button>
            )}
            <ViewModeToggle
              mode={mode}
              onModeChange={onModeChange}
              onPrintAllSlides={mode === 'presentation' ? () => setIsPrinting(true) : undefined}
              onPrintDone={mode === 'presentation' ? () => setIsPrinting(false) : undefined}
            />
            {altLink && (
              <Link
                to={altLink.to}
                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors hidden sm:block ml-2"
              >
                {altLink.label}
              </Link>
            )}
          </div>
        </div>

        {/* Progress bar (presentation only) */}
        {isPresentation && (
          <motion.div
            className="h-0.5 origin-left"
            style={{ backgroundColor: clientBrand?.colors?.primary ?? 'hsl(var(--primary))' }}
            initial={false}
            animate={{ scaleX: progress / 100 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          />
        )}
      </nav>

      {/* Slide region */}
      {isPresentation ? (
        <div className="flex-1 min-h-0">
          <AnimatePresence mode="wait" initial={false}>
            {slides[currentSlide] ? (
              <div key={slides[currentSlide].key} className="w-full h-full overflow-y-auto overflow-x-hidden">
                {slides[currentSlide].component}
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground">
                All slides are hidden. Open the slides panel to show one.
              </div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        <main className="max-w-7xl mx-auto flex-1 print:max-w-none print:w-full">
          {slides.map((slide) => (
            <div
              key={slide.key}
              className="deck-print-slide print:break-after-page print:w-full print:max-w-full print:border-0"
            >
              {slide.component}
            </div>
          ))}
        </main>
      )}

      {/* Slides panel — show/hide for all users; reorder (drag) in dev only */}
      {reorderOpen && onToggleHidden && reorderItems.length > 0 && (
        <Suspense fallback={null}>
          <SlideReorderPanel
            items={reorderItems}
            currentSlideKey={currentSlideKey}
            onReorder={IS_DEV ? onSlideReorder : undefined}
            onToggleHidden={onToggleHidden}
            onNavigate={(idx) => {
              const targetKey = reorderItems[idx]?.key;
              if (!targetKey) return;
              const visibleIdx = slides.findIndex((s) => s.key === targetKey);
              if (visibleIdx >= 0) goTo(visibleIdx);
            }}
            onClose={() => setReorderOpen(false)}
            companyId={companyId}
            canReorder={IS_DEV && Boolean(onSlideReorder)}
          />
        </Suspense>
      )}

      {/* Provider footer — screen only; omitted when white-label */}
      {!whiteLabel && (
        <footer
          className={cn(
            'border-t border-border/30 py-4 px-6 print:hidden',
            isPresentation ? 'shrink-0' : 'mt-16',
          )}
        >
          <div className="max-w-7xl mx-auto flex items-center justify-start">
            <img
              src={navLogo}
              alt={PROVIDER_BRAND.companyName}
              className="h-5"
            />
          </div>
        </footer>
      )}
    </div>
  );
}
